import AutoComplete from 'components/Common/AutoComplete';
import FlowNodeExportConfiguration from './components/FlowNodeExportConfiguration';
import FlowNodeSegmentationConfiguration from './components/FlowNodeSegmentationConfiguration';
import PermissionService from 'components/Common/PermissionService';
import React, { useContext } from 'react';
import useCapacityManagement from 'components/QuestionManagement/ComputeCapacityModal/useCapacityManagement';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Checkbox,
  FormControlLabel,
  FormGroup,
  Grid,
  Radio,
  RadioGroup,
  Skeleton,
  Typography,
} from '@mui/material';
import {
  CLEAN_ROOMS_WITH_EXTENDED_CAPACITY,
  COMPUTE_CAPACITY,
} from 'utils/constants/question.constant';
import { CleanRoomTypes, Ownership } from 'components/CleanRooms/constants';
import { ExpandMore } from '@mui/icons-material';
import { FlowBuilderContext } from 'pages/Flow/FlowBuilder/FlowBuilderContextProvider';
import { HButton, HSpinner } from 'BaseComponents';
import { NODE_ACCESS_TYPE } from 'utils/ignoramusEnums';
import { activeOrgSelector } from 'redux/reducer/AuthenticationReducer';
import { habuColors } from 'Theme';
import {
  productPerm,
  userTypes,
} from 'utils/appConstants';
import { updateSingleNodeData } from 'redux/actions/FlowAction';
import { useAllNodeTags } from 'hooks/useAllNodeTags';
import { useDispatch, useSelector } from 'react-redux';
import { useFetchCleanRoomAssetMapping } from 'hooks/cleanRoomAPIs';
import {
  useFetchNodeDetails,
  useGetFlowNodeAccess,
  useSetNodeTags,
  useUpdateFlowNodeAccess,
} from 'api/flows';
import { useGetCrQuestionV2 } from 'hooks/questionAPIs';


const QuestionSidePanelPrivacy = ({ node, question }) => {
  const dispatch = useDispatch();
  const { crFlowId, crId, flowId } = useContext(FlowBuilderContext);
  const { ID: orgId } = useSelector(activeOrgSelector);
  const [listOfOrgs, setListOfOrgs] = React.useState([]);
  const [orgsEditable, setOrgsEditable] = React.useState(false);
  const [tags, setTags] = React.useState('');
  const [hasTagsChanged, setHasTagsChanged] = React.useState(false);
  const [tagInput, setTagInput] = React.useState('');

  // Refs for segmentation configuration
  const segmentationSubmitRef = React.useRef(null);
  const segmentationShouldSubmitRef = React.useRef(false);
  const segmentationIsSubmittingRef = React.useRef(false);
  const countOfOrgsSelected = listOfOrgs
    .filter((org) => org.accessType === NODE_ACCESS_TYPE.FULL_ACCESS).length;
  const cleanroomDetail = useSelector(state => state.cleanRooms.detail);
  const cleanroomType = cleanroomDetail?.type;
  const allNodeTags = useAllNodeTags();
  const isSuperUser = PermissionService.isAuthorized([userTypes.superuser]);
  const isSupportModeUser = PermissionService.isCXUser();
  const ENABLED_CAPACITY_MANAGEMENT = PermissionService.isProductEnabled(
    [productPerm.CapacityManagement],
  );
  const ENABLED_CAPACITY_MANAGEMENT_SF = PermissionService.isProductEnabled(
    [productPerm.CapacityManagementSnowflakePattern],
  );

  const isCROwner = cleanroomDetail?.ownership === Ownership.OWNER.key;
  const isCapacityManagementEnabled = cleanroomType === CleanRoomTypes.SNOWFLAKE_CR
    ? ENABLED_CAPACITY_MANAGEMENT_SF
    : ENABLED_CAPACITY_MANAGEMENT;

  const allowedToEditComputeCapacity = isSuperUser
    || (isCapacityManagementEnabled && (isCROwner || isSupportModeUser));

  const isFlowSegmentationEnabled = PermissionService.isProductEnabled(
    [productPerm.FlowSegmentation],
  );

  const {
    isLoading, isError, isSuccess, data,
  } = useGetFlowNodeAccess({ crId, crFlowId, nodeId: node.id });
  const {
    isLoading: isFlowNodeAccessUpdating,
    mutate: updateNodeAccess,
  } = useUpdateFlowNodeAccess({ crId, crFlowId, nodeId: node.id });

  const {
    isLoading: isNodeDetailsLoading,
    isSuccess: isNodeDetailsSuccess,
    data: nodeDetails,
    refetch: refetchNodeDetails,
  } = useFetchNodeDetails({ flowId, nodeID: node.id });

  const {
    isLoading: isSetNodeTagsUpdating,
    mutate: setNodeTags,
  } = useSetNodeTags(flowId, node.id);

  const {
    isCapacityUpdating,
    currVal,
    setCurrVal,
    saveCapacity,
  } = useCapacityManagement({
    crId, crqId: node.cleanRoomQuestionID, questionId: question.ID, question,
  });

  // Fetch clean room question details
  const {
    data: cleanRoomQuestionData,
    isLoading: isCleanRoomQuestionLoading,
  } = useGetCrQuestionV2(crId, node?.cleanRoomQuestionID);

  const { isSuccess: isAssetMappingSuccess, data: assetMapping } = useFetchCleanRoomAssetMapping(
    orgId, crId,
  );

  const handleSubmit = () => {
    if (allowedToEditComputeCapacity) {
      saveCapacity();
    }

    if (orgsEditable) {
      updateNodeAccess({ nodeAccess: listOfOrgs });
    }

    if (hasTagsChanged) {
      setNodeTags({ tags, cleanRoomID: crId }, {
        onSuccess: () => {
          setHasTagsChanged(false);
          refetchNodeDetails(); // Refresh node details to show updated tags
          // Update Redux state so tags are available when saving the flow
          dispatch(updateSingleNodeData(node.id, { tags }));
        },
      });
    }

    // Submit segmentation configuration if it should be submitted
    if (segmentationShouldSubmitRef.current && segmentationSubmitRef.current) {
      segmentationSubmitRef.current();
    }
  };

  const selectedTags = React.useMemo(
    () => tags.split(',').map(t => t.trim()).filter(Boolean),
    [tags],
  );

  const selectedTagsArray = React.useMemo(
    () => (Array.isArray(selectedTags) ? selectedTags : []),
    [selectedTags],
  );


  React.useEffect(() => {
    if (isSuccess) {
      setListOfOrgs(data?.data.nodeAccess);
      setOrgsEditable(data?.data.editable);
    }
  }, [data?.data.nodeAccess, data?.data.editable, isSuccess]);

  React.useEffect(() => {
    // Initialize tags from fetched node details
    if (isNodeDetailsSuccess && nodeDetails?.node?.tags) {
      setTags(nodeDetails?.node?.tags ?? '');
      setHasTagsChanged(false); // Reset change tracking when loading existing tags
    }
  }, [isNodeDetailsSuccess, nodeDetails?.node?.tags]);

  const handleOrgAccessChange = (targetOrgId, accessType) => {
    const updatedList = listOfOrgs.map((org) => {
      if (org.organizationID === targetOrgId) {
        return {
          ...org,
          accessType: accessType === NODE_ACCESS_TYPE.NO_ACCESS
            ? NODE_ACCESS_TYPE.FULL_ACCESS
            : NODE_ACCESS_TYPE.NO_ACCESS,
        };
      }
      return org;
    });
    setListOfOrgs(updatedList);
  };

  if (isLoading || isError || isNodeDetailsLoading || isCleanRoomQuestionLoading) {
    return (
      <Grid container spacing={0} sx={{ paddingTop: 3 }}>
        <Grid item xs={12}>
          <Skeleton height={300} />
        </Grid>
      </Grid>
    );
  }

  // Outside of Hybrid/TEE/LinkedIn only snowflake supports Compute Capacity
  // and they only support default or Large. Thus we filter out everything else
  // and only show these options for Hybrid/TEE/LinkedIn.
  const getFilteredComputeCapacity = Object.values(COMPUTE_CAPACITY)
    .filter(item => ![
      COMPUTE_CAPACITY.MEDIUM.value,
      COMPUTE_CAPACITY.XLARGE.value,
      COMPUTE_CAPACITY.XXLARGE.value,
      COMPUTE_CAPACITY.XXXLARGE.value,
    ]
      .includes(item.value)
      || CLEAN_ROOMS_WITH_EXTENDED_CAPACITY.includes(cleanroomType));

  return (
    <>
      <Grid container spacing={1} rowGap={2} sx={{ paddingTop: 4 }}>
        <Accordion
          defaultExpanded
          disableGutters
          elevation={0}
          style={{ width: '100%' }}
        >
          <AccordionSummary
            expandIcon={<ExpandMore />}
            sx={{ background: (theme) => theme.palette.secondary.light }}
          >
            <Typography variant='subtitle1'>Report access</Typography>
          </AccordionSummary>
          <AccordionDetails>
            <Typography variant='body3'>
              Only selected partner organizations can view the output of this node.
            </Typography>
            <Typography variant='subtitle2'>
              {`${countOfOrgsSelected} of ${listOfOrgs.length} partners selected`}
            </Typography>

            {orgsEditable && (
              <FormGroup>
                {listOfOrgs.map((org) => (
                  <FormControlLabel
                    key={org.organizationID}
                    control={<Checkbox checked={org.accessType === NODE_ACCESS_TYPE.FULL_ACCESS} />}
                    onChange={() => handleOrgAccessChange(org.organizationID, org.accessType)}
                    label={org.organizationName}
                  />
                ))}
              </FormGroup>
            )}
          </AccordionDetails>
        </Accordion>
        {allowedToEditComputeCapacity && (
          <Accordion
            defaultExpanded
            disableGutters
            elevation={0}
            style={{ width: '100%' }}
          >
            <AccordionSummary
              expandIcon={<ExpandMore />}
              sx={{ background: (theme) => theme.palette.secondary.light }}
            >
              <Typography variant='subtitle1'>Processing Capacity (Optional)</Typography>
            </AccordionSummary>
            <AccordionDetails>
              <Typography variant='body3'>
                Choose the processing capacity for question runs.
                You can edit this option after publishing the flow.
              </Typography>
              {currVal ? (
                <Grid container>
                  <Grid item xs={12} md={4}>
                    <RadioGroup>
                      {getFilteredComputeCapacity.map(item => (
                        <FormControlLabel
                          key={item.name}
                          value={item.value}
                          control={<Radio checked={currVal === item.value} />}
                          color='primary'
                          onChange={e => setCurrVal(e.target.value)}
                          label={item.name}
                          componentsProps={{
                            typography: { variant: 'body2' },
                          }}
                        />
                      ))}
                    </RadioGroup>
                  </Grid>
                  <Grid item xs={12} md={8}>
                    <Box bgcolor={habuColors.neutral['5']} p={2}>
                      <Typography variant='subtitle2' mb={1}>
                        {COMPUTE_CAPACITY[currVal]?.subtitle}
                      </Typography>
                      <Typography variant='body2'>
                        {COMPUTE_CAPACITY[currVal]?.description}
                      </Typography>
                    </Box>
                  </Grid>
                </Grid>
              ) : <Skeleton height={150} width='100%' variant='rectangular' />}
            </AccordionDetails>
          </Accordion>
        )}
        <Accordion
          defaultExpanded
          disableGutters
          elevation={0}
          style={{ width: '100%' }}
        >
          <AccordionSummary
            expandIcon={<ExpandMore />}
            sx={{ background: (theme) => theme.palette.secondary.light }}
          >
            <Typography variant='subtitle1'>Tags</Typography>
          </AccordionSummary>
          <AccordionDetails>
            <Typography variant='body3' sx={{ marginBottom: 2 }}>
              Add comma-separated tags to help organize and identify this node.
            </Typography>
            <AutoComplete
              multiple
              freeSolo
              openOnFocus
              options={allNodeTags}
              value={selectedTags}
              inputValue={tagInput}
              onInputChange={(_, newInputValue) => {
                setTagInput(newInputValue);
              }}
              onBlur={() => {
                const value = tagInput.trim();
                if (!value) return;
                const newTags = Array.from(new Set([...(selectedTagsArray || []), value]));
                setTags(newTags.join(','));
                setHasTagsChanged(true);
                setTagInput('');
              }}
              onChange={(_, newValue) => {
                setTags(newValue.join(','));
                setHasTagsChanged(true);
              }}
              textFieldProps={{
                fullWidth: true,
                placeholder: 'e.g. campaign, analytics, customer-data',
                variant: 'outlined',
                size: 'small',
                helperText: 'Use commas to separate multiple tags',
              }}
            />
          </AccordionDetails>
        </Accordion>

        {/* Export Configuration */}
        <FlowNodeExportConfiguration node={node} />

        {isFlowSegmentationEnabled && cleanRoomQuestionData?.question?.isUserListQuestion
        && isAssetMappingSuccess && assetMapping?.allowSegmentation && (
          <FlowNodeSegmentationConfiguration
            cleanRoomQuestion={cleanRoomQuestionData?.cleanRoomQuestion}
            node={node}
            onSubmitRef={segmentationSubmitRef}
            shouldSubmitRef={segmentationShouldSubmitRef}
            isSubmittingRef={segmentationIsSubmittingRef}
          />
        )}
      </Grid>
      <Grid container mt={2}>
        <HButton
          variant='contained'
          color='primary'
          onClick={handleSubmit}
          disabled={
            (
              !orgsEditable
              && !allowedToEditComputeCapacity
              && !hasTagsChanged
              && !segmentationShouldSubmitRef.current
            )
            || isCapacityUpdating
            || isFlowNodeAccessUpdating
            || isSetNodeTagsUpdating
            || segmentationIsSubmittingRef.current
          }
          endIcon={(
            isCapacityUpdating
            || isFlowNodeAccessUpdating
            || isSetNodeTagsUpdating
            || segmentationIsSubmittingRef.current
          )
            ? <HSpinner isButton />
            : null}
        >
          Submit
        </HButton>
      </Grid>
    </>
  );
};

export default QuestionSidePanelPrivacy;
